import { defineStore } from 'pinia';
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { supabase, handleRealtimeError } from '@/lib/supabase';
import { useAuthStore } from './auth';
import { useToast } from 'vue-toastification';

export const useNotificationsStore = defineStore('notifications', () => {
  const notifications = ref([]);
  const unreadCount = ref(0);
  const loading = ref(false);
  const error = ref(null);
  const userPresence = ref({});
  const connectionState = ref('disconnected');
  const authStore = useAuthStore();
  const toast = useToast();
  const isInitialized = ref(false);
  const processedNotifications = new Set(); // Track processed notifications

  let notificationChannel = null;
  let presenceChannel = null;

  const initialize = async () => {
    if (!authStore.initialized) {
      console.log('[NotificationsStore] Waiting for auth store to initialize...');
      await new Promise(resolve => {
        const unwatch = watch(() => authStore.initialized, (newValue) => {
          if (newValue) {
            unwatch();
            resolve();
          }
        });
      });
    }

    if (!authStore.user?.id) {
      console.log('[NotificationsStore] No authenticated user');
      return;
    }

    if (isInitialized.value) {
      console.log('[NotificationsStore] Already initialized');
      return;
    }

    try {
      loading.value = true;
      console.log('Initializing notifications store...', {
        userId: authStore.user.id,
        connectionState: connectionState.value
      });
      
      // Cleanup any existing subscription
      if (notificationChannel) {
        console.log('Cleaning up existing notification channel');
        await notificationChannel.unsubscribe();
      }

      // Subscribe to notifications
      notificationChannel = supabase.channel('notifications')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${authStore.user.id}`
          },
          (payload) => {
            console.log('[NotificationsStore] Received postgres change:', payload);
            handleNotificationChange(payload);
          }
        )
        .on('broadcast',
          { event: 'new_notification' },
          (payload) => {
            console.log('[NotificationsStore] Received broadcast:', payload);
            if (payload.payload && payload.payload.user_id === authStore.user.id) {
              handleNotificationChange({
                eventType: 'INSERT',
                new: payload.payload
              });
            }
          }
        )
        .subscribe(async (status) => {
          console.log('[NotificationsStore] Notification subscription status:', status);
          connectionState.value = status === 'SUBSCRIBED' ? 'connected' : 'disconnected';
          
          if (status === 'SUBSCRIBED') {
            console.log('[NotificationsStore] Successfully subscribed to notifications');
            isInitialized.value = true;
            // Fetch initial notifications after successful subscription
            await fetchNotifications();
          } else {
            console.warn('[NotificationsStore] Subscription status:', status);
          }
        });

      // Subscribe to presence
      presenceChannel = supabase.channel('online-users')
        .on('presence', { event: 'sync' }, () => {
          const state = presenceChannel.presenceState();
          userPresence.value = state;
          console.log('[NotificationsStore] Presence sync:', state);
        })
        .on('presence', { event: 'join' }, ({ key, newPresences }) => {
          console.log('[NotificationsStore] User joined:', newPresences);
        })
        .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
          console.log('[NotificationsStore] User left:', leftPresences);
        })
        .subscribe(async (status) => {
          console.log('[NotificationsStore] Presence subscription status:', status);
          if (status === 'SUBSCRIBED') {
            await presenceChannel.track({
              user_id: authStore.user.id,
              online_at: new Date().toISOString(),
              status: 'online'
            });
          }
        });

    } catch (err) {
      error.value = err.message;
      handleRealtimeError(err);
      console.error('[NotificationsStore] Error initializing notifications:', err);
    } finally {
      loading.value = false;
    }
  };

  const handleNotificationChange = (payload) => {
    console.log('[NotificationsStore] Handling notification change:', {
      eventType: payload.eventType,
      payload: payload,
      currentNotifications: notifications.value.length,
      currentUnreadCount: unreadCount.value
    });
    
    const { eventType, new: newNotification, old: oldNotification } = payload;
    
    switch (eventType) {
      case 'INSERT':
        // Check if we've already processed this notification
        if (processedNotifications.has(newNotification.id)) {
          console.log('[NotificationsStore] Skipping duplicate notification:', newNotification.id);
          return;
        }
        
        console.log('[NotificationsStore] Processing INSERT:', {
          notification: newNotification,
          beforeCount: notifications.value.length
        });
        
        // Add to processed set
        processedNotifications.add(newNotification.id);
        
        // Clean up old processed notifications (keep last 100)
        if (processedNotifications.size > 100) {
          const toRemove = Array.from(processedNotifications).slice(0, processedNotifications.size - 100);
          toRemove.forEach(id => processedNotifications.delete(id));
        }
        
        notifications.value.unshift(newNotification);
        unreadCount.value++;
        
        console.log('[NotificationsStore] After INSERT:', {
          notificationsCount: notifications.value.length,
          unreadCount: unreadCount.value,
          latestNotification: notifications.value[0]
        });
        
        // Show toast notification
        let icon = '🔔';
        if (newNotification.type?.startsWith('payment_')) {
          icon = '💰';
        } else if (newNotification.type?.startsWith('order_')) {
          icon = '📦';
        } else if (newNotification.type?.startsWith('expense_')) {
          icon = '💸';
        } else if (newNotification.type?.startsWith('quotation_')) {
          icon = '📄';
        }

        toast.info(newNotification.message, {
          timeout: 5000,
          position: 'top-right',
          closeOnClick: true,
          pauseOnFocusLoss: true,
          pauseOnHover: true,
          draggable: true,
          draggablePercent: 0.6,
          showCloseButtonOnHover: true,
          hideProgressBar: true,
          closeButton: 'button',
          icon,
          rtl: false,
        });

        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('new-notification', {
          detail: { notification: newNotification }
        }));

        // If it's an order status update, dispatch that event too
        if (newNotification.type === 'status_update' && newNotification.data?.order_id) {
          window.dispatchEvent(new CustomEvent('order-status-update', {
            detail: {
              order: {
                id: newNotification.data.order_id,
                status: newNotification.data.new_status,
                order_number: newNotification.data.order_number
              }
            }
          }));
        }
        break;

      case 'UPDATE':
        const index = notifications.value.findIndex(n => n.id === newNotification.id);
        if (index !== -1) {
          notifications.value[index] = newNotification;
          if (!oldNotification.read_at && newNotification.read_at) {
            unreadCount.value = Math.max(0, unreadCount.value - 1);
          }
        }
        break;

      case 'DELETE':
        const deleteIndex = notifications.value.findIndex(n => n.id === oldNotification.id);
        if (deleteIndex !== -1) {
          notifications.value.splice(deleteIndex, 1);
          if (!oldNotification.read_at) {
            unreadCount.value = Math.max(0, unreadCount.value - 1);
          }
        }
        break;
    }
  };

  const fetchNotifications = async (page = 1, limit = 20, filters = {}) => {
    try {
      console.log('[NotificationsStore] Fetching notifications for user:', authStore.user.id, 'with filters:', filters);
      
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', authStore.user.id)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.type) {
        query = query.ilike('type', `%${filters.type}%`);
      }
      
      if (filters.unread === true) {
        query = query.is('read_at', null);
      }
      
      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      console.log('[NotificationsStore] Fetched notifications:', {
        count: data.length,
        notifications: data,
        filters,
        page,
        limit
      });

      // For first page or when filters change, replace all notifications
      if (page === 1) {
        notifications.value = data;
        unreadCount.value = data.filter(n => !n.read_at).length;
      } else {
        // For subsequent pages, append to existing notifications
        const existingIds = new Set(notifications.value.map(n => n.id));
        const newNotifications = data.filter(n => !existingIds.has(n.id));
        notifications.value = [...notifications.value, ...newNotifications];
      }
      
      // Return whether there might be more data
      return data.length === limit;
    } catch (err) {
      error.value = err.message;
      console.error('[NotificationsStore] Error fetching notifications:', err);
      throw err;
    }
  };

  const fetchMoreNotifications = async (offset = 0, limit = 20, filters = {}) => {
    try {
      console.log('[NotificationsStore] Fetching more notifications:', { offset, limit, filters });
      
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', authStore.user.id)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.type) {
        query = query.ilike('type', `%${filters.type}%`);
      }
      
      if (filters.unread === true) {
        query = query.is('read_at', null);
      }
      
      // Apply pagination using range
      query = query.range(offset, offset + limit - 1);

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Append new notifications if they don't already exist
      const existingIds = new Set(notifications.value.map(n => n.id));
      const newNotifications = data.filter(n => !existingIds.has(n.id));
      
      notifications.value = [...notifications.value, ...newNotifications];
      
      return data.length === limit; // Return true if there might be more
    } catch (err) {
      error.value = err.message;
      console.error('[NotificationsStore] Error fetching more notifications:', err);
      return false;
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      console.log('[NotificationsStore] Attempting to mark as read:', notificationId, 'User:', authStore.user.id);
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString(), is_read: true })
        .eq('id', notificationId)
        .eq('user_id', authStore.user.id);

      if (updateError) {
        console.error('[NotificationsStore] Supabase update error:', updateError);
        throw updateError;
      }

      // Update local state
      const notification = notifications.value.find(n => n.id === notificationId);
      if (notification && !notification.read_at) {
        notification.read_at = new Date().toISOString();
        notification.is_read = true;
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      }
      console.log('[NotificationsStore] Marked as read successfully:', notificationId);
      // Refresh notifications from backend to ensure UI is in sync
      await fetchNotifications();
    } catch (err) {
      error.value = err.message;
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString(), is_read: true })
        .eq('user_id', authStore.user.id)
        .is('read_at', null);

      if (updateError) throw updateError;

      // Update local state
      notifications.value.forEach(notification => {
        if (!notification.read_at) {
          notification.read_at = new Date().toISOString();
          notification.is_read = true;
        }
      });
      unreadCount.value = 0;
      await fetchNotifications();
    } catch (err) {
      error.value = err.message;
      console.error('Error marking all notifications as read:', err);
    }
  };

  const updatePresence = async (status) => {
    if (presenceChannel && ['online', 'away', 'offline'].includes(status)) {
      await presenceChannel.track({
        user_id: authStore.user.id,
        online_at: new Date().toISOString(),
        status
      });
    }
  };

  // Cleanup function
  const cleanup = () => {
    console.log('[NotificationsStore] Cleaning up subscriptions');
    if (notificationChannel) {
      notificationChannel.unsubscribe();
      notificationChannel = null;
    }
    if (presenceChannel) {
      presenceChannel.unsubscribe();
      presenceChannel = null;
    }
    connectionState.value = 'disconnected';
    isInitialized.value = false;
  };

  // Initialize on mount
  onMounted(() => {
    console.log('[NotificationsStore] Component mounted, initializing...');
    initialize();
  });

  // Cleanup on unmount
  onUnmounted(() => {
    console.log('[NotificationsStore] Component unmounting, cleaning up...');
    cleanup();
  });

  return {
    notifications,
    unreadCount,
    loading,
    error,
    userPresence,
    connectionState,
    initialize,
    cleanup,
    fetchNotifications,
    fetchMoreNotifications,
    markAsRead,
    markAllAsRead,
    updatePresence
  };
}); 